/**
 * O tema da interface: claro, escuro, ou o que o aparelho pedir.
 *
 * Os tokens de cor já existiam nas duas versões (`src/styles/index.css`): a classe `dark`
 * no `<html>` troca o conjunto inteiro, e nenhuma tela precisa saber em que tema está. O
 * que faltava era quem põe a classe — e é só isso que este módulo faz.
 *
 * A escolha mora no navegador (`localStorage`), e não na conta: tema é do olho e da luz da
 * sala, não da pessoa — quem abre o sistema no celular ao sol e no monitor do escritório
 * quer um em cada aparelho. "Do aparelho" é o padrão, e nem chega a ser gravado: a
 * ausência da chave é o que quer dizer "siga o sistema".
 *
 * A mesma leitura é repetida à mão em `index.html`, antes do primeiro quadro, para a
 * página não piscar clara antes de escurecer. O teste garante que a chave dos dois lados
 * é a mesma.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'obraplan.theme';
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

export function parseThemePreference(raw: unknown): ThemePreference {
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

export interface ThemeSnapshot {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
}

type MediaChangeListener = (event: { readonly matches: boolean }) => void;

/**
 * O que o tema precisa do navegador, nomeado — para o teste entregar dublês e para o
 * jsdom, que não tem `matchMedia`, não derrubar a loja.
 */
export interface ThemeEnvironment {
  /** `localStorage`, ou `undefined` onde o navegador o nega (modo privado antigo). */
  readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined;
  /** `matchMedia(DARK_MEDIA_QUERY)`, ou `undefined` onde não há. */
  readonly media:
    | {
        readonly matches: boolean;
        addEventListener(type: 'change', listener: MediaChangeListener): void;
        removeEventListener(type: 'change', listener: MediaChangeListener): void;
      }
    | undefined;
  /** O `<html>`, que recebe a classe `dark`. */
  readonly root: { readonly classList: Pick<DOMTokenList, 'toggle'> } | undefined;
  /** Quem avisa quando outra aba muda a escolha (`storage`). */
  readonly crossTab:
    | {
        addEventListener(
          type: 'storage',
          listener: (event: { readonly key: string | null }) => void,
        ): void;
        removeEventListener(
          type: 'storage',
          listener: (event: { readonly key: string | null }) => void,
        ): void;
      }
    | undefined;
}

export interface ThemeStore {
  /** Estável enquanto nada muda — é o contrato do `useSyncExternalStore`. */
  getSnapshot: () => ThemeSnapshot;
  subscribe: (listener: () => void) => () => void;
  setPreference: (preference: ThemePreference) => void;
}

export function createThemeStore(env: ThemeEnvironment): ThemeStore {
  const listeners = new Set<() => void>();

  /**
   * A escolha feita nesta aba, mesmo quando o navegador não deixou gravá-la: sem isto, um
   * `localStorage` que recusa escrever faria o botão não responder.
   */
  let chosen: ThemePreference | undefined;

  function readPreference(): ThemePreference {
    if (chosen !== undefined) return chosen;
    try {
      return parseThemePreference(env.storage?.getItem(THEME_STORAGE_KEY));
    } catch {
      return 'system';
    }
  }

  function compute(): ThemeSnapshot {
    const preference = readPreference();
    return { preference, resolved: resolveTheme(preference, env.media?.matches ?? false) };
  }

  let snapshot = compute();
  env.root?.classList.toggle('dark', snapshot.resolved === 'dark');

  function publish(): void {
    const next = compute();
    if (next.preference === snapshot.preference && next.resolved === snapshot.resolved) return;
    snapshot = next;
    env.root?.classList.toggle('dark', next.resolved === 'dark');
    for (const listener of listeners) listener();
  }

  const onMediaChange: MediaChangeListener = () => publish();
  const onStorage = (event: { readonly key: string | null }): void => {
    // Chave nula é o `clear()`; qualquer outra que não seja a nossa não interessa.
    if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
    chosen = undefined;
    publish();
  };

  return {
    getSnapshot: () => snapshot,

    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        env.media?.addEventListener('change', onMediaChange);
        env.crossTab?.addEventListener('storage', onStorage);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          env.media?.removeEventListener('change', onMediaChange);
          env.crossTab?.removeEventListener('storage', onStorage);
        }
      };
    },

    setPreference(preference) {
      chosen = preference;
      try {
        if (preference === 'system') env.storage?.removeItem(THEME_STORAGE_KEY);
        else env.storage?.setItem(THEME_STORAGE_KEY, preference);
      } catch {
        // Sem gravar: vale para esta aba, e a próxima abertura volta ao aparelho.
      }
      publish();
    },
  };
}

/** O ambiente de verdade, com cada acesso ao navegador protegido. */
export function browserThemeEnvironment(): ThemeEnvironment {
  if (typeof window === 'undefined') {
    return { storage: undefined, media: undefined, root: undefined, crossTab: undefined };
  }
  let storage: ThemeEnvironment['storage'];
  try {
    storage = window.localStorage;
  } catch {
    storage = undefined;
  }
  return {
    storage,
    media:
      typeof window.matchMedia === 'function' ? window.matchMedia(DARK_MEDIA_QUERY) : undefined,
    root: document.documentElement,
    crossTab: window,
  };
}

/** A loja da aplicação: uma só, para o seletor e os avisos lerem o mesmo tema. */
export const themeStore: ThemeStore = createThemeStore(browserThemeEnvironment());
